import { useCallback, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { logger } from '../../helpers/logger.helper';
import { TurnstileWidget } from './TurnstileWidget';
import { TURNSTILE_SITE_KEY, TURNSTILE_TEST_SITE_KEY } from './turnstile.constants';
import type { TurnstileWidgetRef } from './TurnstileWidget.types';

const styles = StyleSheet.create({
  hiddenCaptchaHost: {
    position: 'absolute',
    opacity: 0,
    width: 1,
    height: 1,
    pointerEvents: 'none',
  },
  visibleWebCaptchaHost: {
    marginTop: 8,
    alignItems: 'center',
  },
});

const TURNSTILE_EXECUTE_TIMEOUT_MS = 15000;
const TURNSTILE_VISIBLE_TIMEOUT_MS = 120000;

export interface UseCaptchaTokenReturn {
  getCaptchaToken: () => Promise<string>;
  resetCaptcha: () => void;
  CaptchaWidget: ReactElement | null;
  captchaMode: 'invisible' | 'visible';
}

export function useCaptchaToken(): UseCaptchaTokenReturn {
  const captchaRef = useRef<TurnstileWidgetRef | null>(null);
  const inFlightTokenPromiseRef = useRef<Promise<string> | null>(null);
  const warnedTestSiteKeyRef = useRef(false);
  const [captchaMode, setCaptchaMode] = useState<'invisible' | 'visible'>('invisible');

  const resetCaptcha = useCallback(() => {
    captchaRef.current?.reset();
  }, []);

  const getCaptchaToken = useCallback(async () => {
    logger.debug('Turnstile getCaptchaToken start');
    if (!warnedTestSiteKeyRef.current && TURNSTILE_SITE_KEY === TURNSTILE_TEST_SITE_KEY) {
      warnedTestSiteKeyRef.current = true;
      logger.warn(
        'Turnstile is using Cloudflare dummy site key (dev fallback). Supabase must use the matching Turnstile test secret in Auth → Captcha, or use your real site key + secret; otherwise signup returns captcha_failed (invalid-input-response). See https://developers.cloudflare.com/turnstile/troubleshooting/testing/',
      );
    }
    if (inFlightTokenPromiseRef.current) {
      logger.warn('Turnstile execute requested while already executing; reusing in-flight token request');
      return inFlightTokenPromiseRef.current;
    }
    if (!TURNSTILE_SITE_KEY) {
      throw new Error('Turnstile site key is not configured');
    }

    const runExecuteWithTimeout = async (timeoutMs: number): Promise<string> => {
      logger.debug('Turnstile getCaptchaToken execute call', { timeoutMs, mode: captchaMode });
      const executePromise = captchaRef.current?.execute();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error('Turnstile execute timed out'));
        }, timeoutMs);
      });
      const token = await Promise.race([executePromise, timeoutPromise]);
      if (!token) {
        throw new Error('Turnstile token was not generated');
      }
      return token;
    };

    const tokenPromise = (async () => {
      try {
        const token = await runExecuteWithTimeout(TURNSTILE_EXECUTE_TIMEOUT_MS);
        setCaptchaMode('invisible');
        return token;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        if (message !== 'Turnstile execute timed out') {
          throw error;
        }
        logger.warn('Turnstile invisible execute timed out; switching to visible challenge');
        setCaptchaMode('visible');
        captchaRef.current?.reset();
        const token = await runExecuteWithTimeout(TURNSTILE_VISIBLE_TIMEOUT_MS);
        setCaptchaMode('invisible');
        return token;
      }
    })();

    inFlightTokenPromiseRef.current = tokenPromise;
    try {
      return await tokenPromise;
    } catch (error) {
      logger.error('Turnstile getCaptchaToken failed', {
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      captchaRef.current?.reset();
      throw error;
    } finally {
      inFlightTokenPromiseRef.current = null;
    }
  }, [captchaMode]);

  const CaptchaWidget = useMemo(
    () => (
      <View style={Platform.OS === 'web' && captchaMode === 'visible' ? styles.visibleWebCaptchaHost : styles.hiddenCaptchaHost}>
        <TurnstileWidget ref={captchaRef} mode={captchaMode} />
      </View>
    ),
    [captchaMode]
  );

  return {
    getCaptchaToken,
    resetCaptcha,
    CaptchaWidget,
    captchaMode,
  };
}

